import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../lib/api-client';
import { Modal } from '../../components/ui/Modal';
import { PatientUhidAutocomplete } from '../../components/ui/PatientUhidAutocomplete';
import { FieldError } from '../../components/ui/FieldError';
import { confirmBillCreate } from '../../lib/confirm-service';
import { usePatientLookupSearch, usePatientUhidSearch } from '../../lib/patient-uhid-search';
import { useToast } from '../../lib/toast';
import { useCreateOpdRegistration, useOpdAppointment, useOpdLookup } from './opd.queries';
import { FieldLabel, LookupSelect, PatientDemographicFields } from './opd.shared';
import { ageFromDateOfBirth, blankPatientFields, currency, dateTimeInput, patientFieldsFromOption } from './opd.utils';
import type { CreateRegistrationPayload, OpdLookupOption, OpdPayment, PatientFormFields } from './types';

interface DraftCharge { charge: string; name: string; rate: number; quantity: number; discountPercent: number; discountAmount: number; autoDoctorFee?: boolean }
type SaveAction = 'new' | 'close' | 'bill' | 'prescription';

const lineAmount = (line: Pick<DraftCharge, 'rate' | 'quantity' | 'discountPercent' | 'discountAmount'>) => {
  const gross = line.rate * line.quantity;
  return Math.max(0, gross - Math.max(line.discountAmount, gross * line.discountPercent / 100));
};

const validateRegistrationFields = (appointmentDate: string, department: string, doctor: string, patient: PatientFormFields) => {
  const errors: Record<string, string> = {};
  if (!appointmentDate) errors.appointmentDate = 'Appointment Date is required.';
  if (!department) errors.department = 'Department is required.';
  if (!doctor) errors.doctor = 'Consultant Doctor is required.';
  if (!(patient.phone ?? '').trim()) errors.phone = 'Phone is required.';
  else if (!/^\d{10}$/.test((patient.phone ?? '').trim())) errors.phone = 'Enter a 10 digit phone number.';
  if (!patient.name.trim()) errors.patientName = "Patient's name is required.";
  if (!patient.gender) errors.gender = 'Gender is required.';
  if (!(patient.address ?? '').trim()) errors.address = 'Address is required.';
  if (!(patient.state ?? '').trim()) errors.state = 'State is required.';
  return errors;
};

export const OpdRegistrationPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { appointmentId } = useParams();
  const appointment = useOpdAppointment(appointmentId);
  const mutation = useCreateOpdRegistration();
  const departments = useOpdLookup('departments');
  const referrals = useOpdLookup('referrals');
  const providers = useOpdLookup('providers');
  const marketByOptions = useOpdLookup('market-by');
  const [department, setDepartment] = useState('');
  const doctors = useOpdLookup('doctors', '', department);
  const [doctor, setDoctor] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const debouncedPatientSearch = usePatientUhidSearch(patientSearch);
  const patients = useOpdLookup('patients', debouncedPatientSearch);
  const [patient, setPatient] = useState<PatientFormFields>(blankPatientFields);
  const debouncedPhoneSearch = usePatientLookupSearch(patient.phone ?? '');
  const debouncedNameSearch = usePatientLookupSearch(patient.name);
  const phonePatients = useOpdLookup('appointment-patients-phone', debouncedPhoneSearch);
  const namePatients = useOpdLookup('appointment-patients-name', debouncedNameSearch);
  const states = useOpdLookup('states');
  const districts = useOpdLookup('districts', '', '', patient.state ?? '');
  const [appointmentDate, setAppointmentDate] = useState(dateTimeInput);
  const [referredBy, setReferredBy] = useState('');
  const [provider, setProvider] = useState('');
  const [marketBy, setMarketBy] = useState('');
  const charges = useOpdLookup('charges');
  const [chargeId, setChargeId] = useState('');
  const [draftRate, setDraftRate] = useState(0);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftDiscountPercent, setDraftDiscountPercent] = useState(0);
  const [draftDiscountAmount, setDraftDiscountAmount] = useState(0);
  const [lines, setLines] = useState<DraftCharge[]>([]);
  const [payments, setPayments] = useState<OpdPayment[]>([
    { mode: 'Cash', bankName: null, amount: 0 },
    { mode: 'UPI', bankName: 'Axis Bank', amount: 0 },
    { mode: 'Cheque', bankName: 'Axis Bank', amount: 0 },
    { mode: 'Online', bankName: 'Axis Bank', amount: 0 },
  ]);
  const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('flat');
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [saveAction, setSaveAction] = useState<SaveAction>('close');
  const [prefilled, setPrefilled] = useState(false);
  const [showChargeAlert, setShowChargeAlert] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!appointment.data || prefilled) return;
    const row = appointment.data;
    setDepartment(row.department.id);
    setDoctor(row.doctor.id);
    setAppointmentDate(new Date(new Date(row.appointmentDate).getTime() - new Date(row.appointmentDate).getTimezoneOffset() * 60_000).toISOString().slice(0, 16));
    setPatient({ patientId: row.patient?.id ?? null, ...row.patientSnapshot });
    setPatientSearch(row.patientSnapshot.uhid ?? '');
    setPrefilled(true);
  }, [appointment.data, prefilled]);

  const total = useMemo(() => lines.reduce((sum, line) => sum + lineAmount(line), 0), [lines]);
  const discountAmount = discountType === 'percentage' ? total * totalDiscount / 100 : totalDiscount;
  const grandTotal = Math.max(0, total - discountAmount);
  const totalPayment = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const errors = showErrors ? validateRegistrationFields(appointmentDate, department, doctor, patient) : {};
  const selectedDoctor = useMemo(() => doctors.data?.find((item) => item.id === doctor), [doctor, doctors.data]);

  useEffect(() => {
    const consultationCharge = selectedDoctor?.consultationCharge;
    setLines((current) => {
      const withoutDoctorFee = current.filter((line) => !line.autoDoctorFee);
      if (!doctor || !consultationCharge?.id) return withoutDoctorFee;
      if (withoutDoctorFee.some((line) => line.charge === consultationCharge.id)) return withoutDoctorFee;
      return [
        {
          charge: consultationCharge.id,
          name: consultationCharge.label,
          rate: consultationCharge.rate,
          quantity: 1,
          discountPercent: 0,
          discountAmount: 0,
          autoDoctorFee: true,
        },
        ...withoutDoctorFee,
      ];
    });
  }, [doctor, selectedDoctor?.consultationCharge?.id, selectedDoctor?.consultationCharge?.label, selectedDoctor?.consultationCharge?.rate]);

  const chooseDoctor = (id: string) => {
    setDoctor(id);
    const selected = doctors.data?.find((item) => item.id === id);
    if (selected?.departmentId) setDepartment(selected.departmentId);
  };
  const choosePatient = (id: string) => {
    const option = patients.data?.find((item) => item.id === id);
    if (!option) { setPatient(blankPatientFields()); return; }
    setPatient(patientFieldsFromOption(option));
  };
  const changePatientSearch = (value: string) => { setPatientSearch(value); if (patient.patientId) setPatient(blankPatientFields()); };
  const choosePatientFrom = (options: OpdLookupOption[] | undefined, id: string) => {
    const option = options?.find((item) => item.id === id);
    if (!option) {
      setPatient(blankPatientFields());
      setPatientSearch('');
      return;
    }
    setPatient(patientFieldsFromOption(option));
    setPatientSearch(option.code ?? '');
  };
  const changePatientField = (field: 'name' | 'phone', value: string) => {
    if (patient.patientId) setPatientSearch('');
    setPatient((current) => {
      const next = current.patientId ? blankPatientFields() : current;
      return { ...next, [field]: field === 'phone' ? value || null : value };
    });
  };
  const setDob = (dob: string) => {
    if (!dob) { setPatient((current) => ({ ...current, dateOfBirth: null })); return; }
    setPatient((current) => ({ ...current, dateOfBirth: dob, ...ageFromDateOfBirth(dob) }));
  };

  const resetDraft = () => { setChargeId(''); setDraftRate(0); setDraftQuantity(1); setDraftDiscountPercent(0); setDraftDiscountAmount(0); };
  const chooseCharge = (id: string) => {
    setChargeId(id);
    const selected = charges.data?.find((item) => item.id === id);
    setDraftRate(selected?.rate ?? 0);
    setDraftQuantity(1);
    setDraftDiscountPercent(0);
    setDraftDiscountAmount(0);
  };
  const draftAmount = lineAmount({ rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount });
  const addCharge = () => {
    const selected = charges.data?.find((item) => item.id === chargeId);
    if (!selected || lines.some((line) => line.charge === selected.id)) return;
    setLines((current) => [...current, { charge: selected.id, name: selected.label, rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount }]);
    resetDraft();
  };

  const updateLine = (index: number, patch: Partial<DraftCharge>) => setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));
  const updatePayment = (index: number, patch: Partial<OpdPayment>) => setPayments((current) => current.map((payment, paymentIndex) => paymentIndex === index ? { ...payment, ...patch } : payment));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (lines.length === 0) {
      showToast('Please add at least one charge before saving OPD registration.', 'warning');
      setShowChargeAlert(true);
      return;
    }
    setShowErrors(true);
    if (Object.keys(validateRegistrationFields(appointmentDate, department, doctor, patient)).length > 0) {
      showToast('Please fix the highlighted fields before saving OPD registration.', 'warning');
      return;
    }
    if (!(await confirmBillCreate())) return;
    const payload: CreateRegistrationPayload = {
      ...patient,
      appointmentDate,
      department,
      doctor,
      enquiry: appointmentId ?? null,
      referredBy: referredBy || null,
      provider: provider || null,
      marketBy: marketBy || null,
      charges: lines.map((line) => ({ charge: line.charge, rate: line.rate, quantity: line.quantity, discountPercent: line.discountPercent, discountAmount: line.discountAmount })),
      payments: payments.filter((payment) => payment.amount > 0),
      discountType,
      totalDiscount,
    };
    try {
      const created = await mutation.mutateAsync(payload);
      showToast('OPD registration saved successfully.', 'success');
      if (saveAction === 'new') {
        setPatient(blankPatientFields()); setPatientSearch(''); setLines([]); setPayments([{ mode: 'Cash', bankName: null, amount: 0 }, { mode: 'UPI', bankName: 'Axis Bank', amount: 0 }]); setTotalDiscount(0); setAppointmentDate(dateTimeInput()); setShowErrors(false);
      } else if (saveAction === 'bill') {
        void navigate(`/opd/${created.id}/print-bill`);
      } else if (saveAction === 'prescription') {
        void navigate(`/opd/${created.id}/print-prescription?header=0`);
      } else {
        void navigate('/opd');
      }
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'OPD registration could not be saved.', 'error');
    }
  };

  const busy = mutation.isPending || appointment.isLoading;
  return (
    <div className="page-stack opd-page">
      <div className="page-heading"><div><p className="eyebrow">OPD</p><h1>OPD Registration</h1><p>Patient details, consultation charges, discounts, and payment.</p></div><Link className="button button-secondary" to="/opd">Back to OPD List</Link></div>
      <form className="content-card opd-registration-card" noValidate onSubmit={(event) => void submit(event)}>
        <section className="opd-section"><h2>OPD Registration</h2>
          <div className="opd-reg-row1">
            <label><FieldLabel required>Appointment Date</FieldLabel><input type="datetime-local" value={appointmentDate} onChange={(event) => setAppointmentDate(event.target.value)} required aria-invalid={Boolean(errors.appointmentDate)} /><FieldError message={errors.appointmentDate} /></label>
            <label><FieldLabel required>Department</FieldLabel><LookupSelect id="opd-registration-department" value={department} options={departments.data} placeholder="Select" emptyOptionLabel="Select" clearable={false} ariaInvalid={Boolean(errors.department)} onChange={(value) => { setDepartment(value); setDoctor(''); }} /><FieldError message={errors.department} /></label>
            <label><FieldLabel required>Consultant Doctor</FieldLabel><LookupSelect id="opd-registration-doctor" value={doctor} options={doctors.data} placeholder="Select Doctor" clearable={false} ariaInvalid={Boolean(errors.doctor)} onChange={chooseDoctor} /><FieldError message={errors.doctor} /></label>
            <label><FieldLabel>UHID</FieldLabel><PatientUhidAutocomplete id="opd-registration-patient" value={patientSearch} selectedId={patient.patientId} options={patients.data} isFetching={patients.isFetching} isError={patients.isError} placeholder="Search by UHID" onInputChange={changePatientSearch} onChange={choosePatient} /></label>
            <label><FieldLabel required>Patient&apos;s name</FieldLabel><PatientUhidAutocomplete id="opd-registration-patient-name" mode="name" value={patient.name} selectedId={patient.patientId} options={namePatients.data} isFetching={namePatients.isFetching} isError={namePatients.isError} placeholder="Type patient name" ariaInvalid={Boolean(errors.patientName)} onInputChange={(value) => changePatientField('name', value)} onChange={(id) => choosePatientFrom(namePatients.data, id)} /><FieldError message={errors.patientName} /></label>
            <label><FieldLabel required>Phone</FieldLabel><PatientUhidAutocomplete id="opd-registration-patient-phone" mode="phone" value={patient.phone ?? ''} selectedId={patient.patientId} options={phonePatients.data} isFetching={phonePatients.isFetching} isError={phonePatients.isError} placeholder="Type patient phone number" ariaInvalid={Boolean(errors.phone)} onInputChange={(value) => changePatientField('phone', value)} onChange={(id) => choosePatientFrom(phonePatients.data, id)} /><FieldError message={errors.phone} /></label>
            <label><FieldLabel required>Gender</FieldLabel><select value={patient.gender} onChange={(event) => setPatient((current) => ({ ...current, gender: event.target.value as PatientFormFields['gender'] }))} required aria-invalid={Boolean(errors.gender)}><option value="" disabled>Select Gender</option><option>Male</option><option>Female</option><option>Others</option></select><FieldError message={errors.gender} /></label>
            <label><FieldLabel>DOB</FieldLabel><input type="date" value={patient.dateOfBirth ?? ''} onChange={(event) => setDob(event.target.value)} /></label>
          </div>
          <div className="opd-reg-row2">
            <label><FieldLabel>Referred By</FieldLabel><LookupSelect id="opd-referred-by" value={referredBy} options={referrals.data} placeholder="Select" onChange={setReferredBy} /></label>
            <label><FieldLabel>Provider</FieldLabel><LookupSelect id="opd-provider" value={provider} options={providers.data} placeholder="Select" onChange={setProvider} /></label>
            <label><FieldLabel>Market By</FieldLabel><LookupSelect id="opd-market-by" value={marketBy} options={marketByOptions.data} placeholder="Select" onChange={setMarketBy} /></label>
            <PatientDemographicFields value={patient} onChange={setPatient} showLocation states={states.data} districts={districts.data} errors={errors} />
          </div>
        </section>
        <section className="opd-section"><h2>Charges</h2>
          <div className="table-scroll">
            <table className="opd-charge-table">
              <thead><tr><th>Charge Name<span className="opd-required">*</span></th><th>Rate (₹)<span className="opd-required">*</span></th><th className="opd-charge-narrow">#</th><th>Qty<span className="opd-required">*</span></th><th>Dis (%)<span className="opd-required">*</span></th><th>Dis Amount (₹)<span className="opd-required">*</span></th><th>Amount (₹)<span className="opd-required">*</span></th><th className="opd-charge-narrow" /></tr></thead>
              <tbody>
                <tr className="opd-draft-row">
                  <td><LookupSelect id="opd-charge" value={chargeId} options={charges.data} placeholder="Select" onChange={chooseCharge} /></td>
                  <td><input type="number" min={0} step="0.01" value={draftRate} onChange={(event) => setDraftRate(Number(event.target.value))} /></td>
                  <td className="opd-charge-narrow"><button className="opd-add" type="button" onClick={addCharge} disabled={!chargeId}>+</button></td>
                  <td><input type="number" min={1} value={draftQuantity} onChange={(event) => setDraftQuantity(Number(event.target.value))} /></td>
                  <td><input type="number" min={0} max={100} step="0.01" value={draftDiscountPercent} onChange={(event) => setDraftDiscountPercent(Number(event.target.value))} /></td>
                  <td><input type="number" min={0} step="0.01" value={draftDiscountAmount} onChange={(event) => setDraftDiscountAmount(Number(event.target.value))} /></td>
                  <td><strong>{currency(draftAmount)}</strong></td>
                  <td className="opd-charge-narrow"><button className="opd-add" type="button" onClick={addCharge} disabled={!chargeId}>+</button></td>
                </tr>
                {lines.map((line, index) => (
                  <tr key={line.charge}>
                    <td>{line.name}</td>
                    <td><input type="number" min={0} step="0.01" value={line.rate} onChange={(event) => updateLine(index, { rate: Number(event.target.value) })} /></td>
                    <td className="opd-charge-narrow" />
                    <td><input type="number" min={1} value={line.quantity} onChange={(event) => updateLine(index, { quantity: Number(event.target.value) })} /></td>
                    <td><input type="number" min={0} max={100} step="0.01" value={line.discountPercent} onChange={(event) => updateLine(index, { discountPercent: Number(event.target.value) })} /></td>
                    <td><input type="number" min={0} step="0.01" value={line.discountAmount} onChange={(event) => updateLine(index, { discountAmount: Number(event.target.value) })} /></td>
                    <td><strong>{currency(lineAmount(line))}</strong></td>
                    <td className="opd-charge-narrow"><button type="button" onClick={() => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index))} aria-label={`Remove ${line.name}`}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="opd-section opd-payment-section"><h2>Payment</h2>
          <div className="opd-payment-row">
            <div className="opd-payment-methods">
              <div className="opd-payment-grid">
                <div><label>Payment Mode<select value="Cash" disabled><option>Cash</option></select></label><label>Payment Amount<input type="number" min={0} step="0.01" value={payments[0]?.amount ?? 0} onChange={(event) => updatePayment(0, { amount: Number(event.target.value) })} /></label></div>
              </div>
              <div className="opd-payment-grid">
                <div><label>Mode<select value={payments[3]?.mode ?? 'Online'} onChange={(event) => updatePayment(3, { mode: event.target.value as OpdPayment['mode'] })}><option>UPI</option><option>Transfer to Bank Account</option><option>Cheque</option><option>Other</option><option>Online</option></select></label><label>Bank<select value={payments[3]?.bankName ?? ''} onChange={(event) => updatePayment(3, { bankName: event.target.value })}><option>Axis Bank</option><option>Canara Bank</option><option>State Bank</option></select></label><label>Amount<input type="number" min={0} step="0.01" value={payments[3]?.amount ?? 0} onChange={(event) => updatePayment(3, { amount: Number(event.target.value) })} /></label></div>
              </div>
            </div>
            <div className="opd-total-panel"><div><span>Total</span><strong>{currency(total)}</strong></div><div><span>Discount</span><select value={discountType} onChange={(event) => setDiscountType(event.target.value as 'flat' | 'percentage')}><option value="flat">Rs.</option><option value="percentage">%</option></select><input type="number" min={0} step="0.01" value={totalDiscount} onChange={(event) => setTotalDiscount(Number(event.target.value))} /></div><div><span>Grand Total</span><strong>{currency(grandTotal)}</strong></div><div><span>Total Payment</span><strong>{currency(totalPayment)}</strong></div><div><span>Due / Credit</span><strong className={totalPayment < grandTotal ? 'due' : 'credit'}>{totalPayment < grandTotal ? `Due ${currency(grandTotal - totalPayment)}` : `Credit ${currency(totalPayment - grandTotal)}`}</strong></div></div>
          </div>
        </section>
        <div className="opd-form-actions"><button className="button button-primary" type="submit" disabled={busy} onClick={() => setSaveAction('new')}>Save &amp; New</button><button className="button button-primary" type="submit" disabled={busy} onClick={() => setSaveAction('close')}>Save &amp; Close</button><button className="button button-primary" type="submit" disabled={busy} onClick={() => setSaveAction('bill')}>Save &amp; Bill Print</button><button className="button button-primary" type="submit" disabled={busy} onClick={() => setSaveAction('prescription')}>Save &amp; Prescription Print</button></div>
      </form>
      <Modal open={showChargeAlert} onClose={() => setShowChargeAlert(false)} alert labelledBy="charge-alert-title" describedBy="charge-alert-description" className="confirm-modal">
        <span className="confirm-modal-icon" aria-hidden="true">!</span>
        <h2 id="charge-alert-title">Charge required</h2>
        <p id="charge-alert-description">Please add at least one charge before saving the OPD registration.</p>
        <div className="bed-modal-actions confirm-modal-actions">
          <button className="button button-primary" type="button" autoFocus onClick={() => setShowChargeAlert(false)}>OK</button>
        </div>
      </Modal>
    </div>
  );
};
